import React, { useEffect, useMemo, useRef, useState } from "react";
import { API } from "../api";
import { useEnrichGate } from "../hooks/useEnrichGate";
import { GateStatus } from "./GateStatus";
import { LinkPreview } from "./LinkPreview";
import {
  ShieldCheck,
  ShieldX,
  ThumbsUp,
  ThumbsDown,
  Ban,
  ArrowLeftRight,
  Copy,
  Mail,
  Plus,
  Trash2,
  Check,
  Loader2,
  Sparkles,
  X,
} from "lucide-react";

const SAMPLE =
  "Open hero.png and tweak the landing page banner. Then jump into the login thing to fix the JWT guard. " +
  "Double-check the authentication logic before release. " +
  "I selected that photo earlier, and the file I selected still needs review. Ask my teammate about the api docs. " +
  "Our vector store is slow; the vector store needs an index soon.";

export default function LiveStudio({ sid, strategies, config, memories, memoryRevision, reloadMemories, onEvent }) {
  const [text, setText] = useState(SAMPLE);
  const [strategy, setStrategy] = useState("hybrid_rrf");
  const [profile, setProfile] = useState(config.heuristics.default_profile);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [bindings, setBindings] = useState([]);
  const [emailOpen, setEmailOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const live = useRef({ text: SAMPLE, strategy: "hybrid_rrf", profile: config.heuristics.default_profile, version: 0 });
  const mounted = useRef(true);
  const initial = useRef(false);
  const observedMemoryRevision = useRef(memoryRevision);

  const memMap = useMemo(() => Object.fromEntries(memories.map((m) => [m.id, m])), [memories]);

  const loadBindings = async () => {
    try { setBindings(await API.bindings(sid)); }
    catch (_) { setError("Could not load working context. Please retry."); }
  };
  useEffect(() => {
    loadBindings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  const runEnrich = async (t, meta) => {
    if (!t.trim()) {
      setResult(null);
      return;
    }
    setLoading(true);
    setError("");
    const snapshot = { ...live.current };
    try {
      const r = await API.enrich(sid, t, snapshot.strategy, snapshot.profile, meta);
      if (r.memory_changes?.some((c) => c.action === "created")) await reloadMemories();
      if (!mounted.current || live.current.version !== snapshot.version || live.current.text !== t) return null;
      setResult(r); onEvent(r);
      return r;
    } catch (e) {
      if (mounted.current && live.current.version === snapshot.version) setError(e.response?.data?.detail || "Check failed. New text will be checked automatically.");
      throw e;
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  const gate = useEnrichGate(config.heuristics.call_layer, config.heuristics.lexicon.stopwords, runEnrich);
  useEffect(() => {
    if (observedMemoryRevision.current === memoryRevision) return;
    observedMemoryRevision.current = memoryRevision;
    live.current = { ...live.current, version: live.current.version + 1 };
    setResult(null); setSelected(null); onEvent(null);
    loadBindings();
    gate.force(live.current.text, "palace_changed");
    // Only explicit Palace mutations, never a same-request learning reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoryRevision]);
  useEffect(() => {
    mounted.current = true;
    if (!initial.current) { initial.current = true; gate.onEdit(SAMPLE, SAMPLE.length); }
    return () => { mounted.current = false; };
    // Mount the labelled example once, never on every render or tab switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const edit = (value, cursor) => {
    live.current = { ...live.current, text: value, version: live.current.version + 1 };
    setText(value); setResult(null); setSelected(null); setError(""); setFeedbackMessage("");
    gate.onEdit(value, cursor);
  };
  const changeOption = (key, value) => {
    live.current = { ...live.current, [key]: value, version: live.current.version + 1 };
    if (key === "strategy") setStrategy(value); else setProfile(value);
    setResult(null); setSelected(null);
    gate.force(text, `${key}_changed`);
  };

  const currentStrat = strategies.find((s) => s.id === strategy);

  const sendFeedback = async (memory_id, phrase, vote, suppress = false) => {
    try {
    await API.feedback({ session_id: sid, memory_id, phrase, vote, suppress });
    setFeedbackMessage(suppress ? "Phrase suppressed for this memory" : "Feedback saved");
    if (suppress) {
      await reloadMemories();
      gate.force(text, "suppression_changed");
      setSelected(null);
    }
      return true;
    } catch (_) { setError("Could not save feedback. Please try again."); return false; }
  };

  const swapTo = async (node, altMemId) => {
    const version = live.current.version;
    if (!await sendFeedback(node.memory_id, node.text, "down")) return;
    if (!await sendFeedback(altMemId, node.text, "up")) return;
    if (!mounted.current || version !== live.current.version || !result) return;
    const alternatives = [{ memory_id: node.memory_id, score: node.confidence },
      ...(node.alternatives || []).filter((a) => a.memory_id !== altMemId)];
    const change = { memory_id: altMemId, ambiguous: false, alternatives };
    const updated = { ...result, nodes: result.nodes.map((n) => n.link_id === node.link_id ? { ...n, ...change } : n),
      metadata: { ...result.metadata, links: { ...result.metadata.links,
        [node.link_id]: { ...result.metadata.links[node.link_id], ...change, cluster_id: memMap[altMemId]?.cluster_id || null } } } };
    setSelected({ ...node, ...change });
    setResult(updated);
    onEvent(updated);
  };

  return (
    <div className="studio-grid grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_300px] gap-4">
      {/* editor */}
      <section className="bg-panel border border-line rounded-xl p-4 flex flex-col" data-testid="editor-panel">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h2 className="font-display font-semibold text-sm text-muted">Input <span className="text-[10px] font-mono" data-testid="sample-label">· editable example</span></h2>
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <label className="text-[11px] text-muted font-mono">strategy</label>
            <select
              data-testid="strategy-select"
              value={strategy}
              onChange={(e) => changeOption("strategy", e.target.value)}
              className="bg-panel2 border border-line rounded-lg px-2.5 py-1.5 text-xs font-mono text-fg focus:border-amber outline-none"
            >
              {strategies.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.id}
                  {s.requires_llm ? " (LLM)" : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2 mb-3 text-xs text-muted">
          <label htmlFor="profile-select">Profile</label>
          <select id="profile-select" data-testid="profile-select" value={profile} onChange={(e) => changeOption("profile", e.target.value)} className="bg-panel2 border border-line rounded px-2 py-1 text-fg">
            {Object.entries(config.heuristics.trigger_layer.profiles).map(([id, p]) => <option key={id} value={id}>{p.label}</option>)}
          </select>
          <span data-testid="automatic-mode" className="ml-auto text-teal">● Auto</span>
        </div>
        <textarea
          data-testid="input-textarea"
          value={text}
          onChange={(e) => edit(e.target.value, e.target.selectionStart)}
          maxLength={20000}
          aria-label="Transcript"
          placeholder="Type or dictate here…"
          className="flex-1 min-h-[280px] bg-ink border border-line rounded-lg p-3.5 text-[14px] leading-relaxed text-fg resize-none outline-none focus:border-teal/60"
        />
        <div className="flex flex-wrap items-center gap-3 mt-3 text-[11px] font-mono text-muted">
          <span data-testid="character-count">{text.length} / 20,000 chars</span>
          <span>·</span>
          <span data-testid="active-matchers">{currentStrat ? `matchers: ${currentStrat.matchers.join(", ")}` : ""}</span>
          {loading && <Loader2 size={13} className="animate-spin ml-auto text-amber" />}
        </div>
        <GateStatus stats={gate.stats} loading={loading} result={result} config={config.heuristics.call_layer} />
        {error && <div role="alert" data-testid="enrichment-error" className="text-xs text-rose mt-3">{error}</div>}
      </section>

      {/* enriched view */}
      <section className="bg-panel border border-line rounded-xl p-4 flex flex-col" data-testid="enriched-panel">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h2 className="font-display font-semibold text-sm text-muted uppercase tracking-wider">Enriched View</h2>
          {result?.invariant && (
            <span
              data-testid="invariant-badge"
              className={`flex items-center gap-1.5 text-[11px] font-mono px-2.5 py-1 rounded-full ${
                result.invariant.intact ? "bg-lime/10 text-lime" : "bg-rose/15 text-rose"
              }`}
              title={`original ${result.invariant.original_sha256.slice(0, 12)}… / reconstructed ${result.invariant.reconstructed_sha256.slice(0, 12)}…`}
            >
              {result.invariant.intact ? <ShieldCheck size={13} /> : <ShieldX size={13} />}
              {result.invariant.intact ? "Original text intact ✓" : "INVARIANT BROKEN"}
              <span className="opacity-60">{result.invariant.original_sha256.slice(0, 8)}</span>
            </span>
          )}
        </div>

        <div
          data-testid="enriched-render"
          className="flex-1 min-h-[280px] bg-ink border border-line rounded-lg p-3.5 text-[14px] leading-[1.9] overflow-auto whitespace-pre-wrap break-words"
        >
          {result?.nodes ? (
            result.nodes.map((n, i) => {
              if (n.type === "text") return <span key={i}>{n.text}</span>;
              if (n.type === "deixis")
                return <LinkPreview key={`${result.rev}-${i}`} node={n} memory={memMap[n.resolved_memory_id]}
                  className="deixis-span" testId={`deixis-span-${i}`} />;
              const cls = n.ambiguous ? "link-ambiguous" : n.match_kind === "conceptual" ? "link-conceptual" : "link-surface";
              return (
                <LinkPreview key={`${result.rev}-${i}`} node={n} memory={memMap[n.memory_id]}
                  testId={`link-span-${n.link_id}`} className={`link-span ${cls}`} onSelect={(node) => setSelected({ ...node })}
                  strategy={`${currentStrat?.id} · v${currentStrat?.version}`} />
              );
            })
          ) : (
            <span data-testid="pending-transcript" className="text-muted">{text || "Enriched output appears here…"}</span>
          )}
        </div>

        {/* why card / detail */}
        {selected && (
          <WhyCard
            node={selected}
            memMap={memMap}
            onClose={() => setSelected(null)}
            onFeedback={sendFeedback}
            onSwap={swapTo}
          />
        )}
        {feedbackMessage && <div data-testid="feedback-confirmation" className="text-xs text-lime mt-2" role="status">{feedbackMessage}</div>}

        <div className="flex items-center gap-2 mt-3 text-[11px] font-mono text-muted flex-wrap">
          <span className="flex items-center gap-1"><i className="inline-block w-3 h-0.5 bg-teal" /> surface</span>
          <span className="flex items-center gap-1"><i className="inline-block w-3 h-0.5 bg-amber" /> conceptual</span>
          <span className="flex items-center gap-1"><i className="inline-block w-3 h-0.5 bg-rose" /> ambiguous</span>
          <span className="flex items-center gap-1"><i className="inline-block w-3 h-0.5 bg-lime" /> deixis</span>
          {result?.llm_rerank_active && <span className="text-lime ml-2">· LLM rerank active</span>}
        </div>
      </section>

      {/* side panels */}
      <aside className="flex flex-col gap-4">
        <WorkingContext sid={sid} memories={memories} bindings={bindings} reload={async () => { await loadBindings(); gate.force(text, "binding_changed"); }} />
        <NotesFeed
          sid={sid}
          result={result}
          text={text}
          reloadMemories={reloadMemories}
          rerun={() => gate.force(text, "memory_changed")}
        />
        <ContextPack
          sid={sid}
          text={text}
          strategy={strategy}
          profile={profile}
          copied={copied}
          setCopied={setCopied}
          emailConfigured={config.email_configured}
          openEmail={() => setEmailOpen(true)}
        />
      </aside>

      {emailOpen && (
        <EmailModal sid={sid} text={text} strategy={strategy} profile={profile} onClose={() => setEmailOpen(false)} />
      )}
    </div>
  );
}

function ScoreBar({ label, value, color }) {
  return (
    <div className="flex items-center gap-2" data-testid={`score-${label}`}>
      <span className="w-16 text-muted">{label}</span>
      <div className="flex-1 h-1.5 bg-line rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${Math.round(value * 100)}%`, background: color }} />
      </div>
      <span className="w-9 text-right tabular-nums">{value.toFixed(2)}</span>
    </div>
  );
}

function WhyCard({ node, memMap, onClose, onFeedback, onSwap }) {
  const mem = memMap[node.memory_id] || {};
  const ms = node.matcher_scores || {};
  return (
    <div className="mt-3 bg-panel2 border border-line rounded-lg p-3.5 fadein [overflow-wrap:anywhere]" data-testid="why-card">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] text-muted font-mono uppercase">{node.match_kind} link</div>
          <div data-testid="linked-memory-name" className="font-display font-semibold text-amber">{mem.canonical}</div>
          <div data-testid="linked-memory-pointer" className="text-xs text-muted font-mono break-all">
            {mem.type} · {mem.pointer}
          </div>
        </div>
        <button onClick={onClose} className="text-muted hover:text-fg shrink-0" data-testid="whycard-close">
          <X size={16} />
        </button>
      </div>
      <p className="text-[13px] text-fg/80 mt-1.5">{mem.description}</p>
      <div className="mt-3 space-y-1 text-[11px] font-mono">
        <ScoreBar label="exact" value={ms.exact || 0} color="#3fd0c9" />
        <ScoreBar label="fuzzy" value={ms.fuzzy || 0} color="#3fd0c9" />
        <ScoreBar label="phonetic" value={ms.phonetic || 0} color="#b6e36b" />
        <ScoreBar label="semantic" value={ms.semantic || 0} color="#f6b545" />
        <div className="flex items-center justify-between pt-1 text-muted">
          <span>confidence</span>
          <span data-testid="link-confidence" className="text-fg">{(node.confidence || 0).toFixed(3)}</span>
        </div>
      </div>

      {node.alternatives?.length > 0 && (
        <div className="mt-3">
          <div className="text-[11px] text-muted font-mono mb-1 flex items-center gap-1">
            <ArrowLeftRight size={12} /> swap to alternative
          </div>
          <div className="flex flex-wrap gap-1.5">
            {node.alternatives.map((a) => (
              <button
                key={a.memory_id}
                data-testid={`swap-alt-${a.memory_id}`}
                onClick={() => onSwap(node, a.memory_id)}
                className="text-[11px] px-2 py-1 rounded border border-line hover:border-amber text-fg/80 max-w-full [overflow-wrap:anywhere]"
              >
                {memMap[a.memory_id]?.canonical || a.memory_id} · {a.score.toFixed(2)}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center gap-2">
        <button
          data-testid="fb-up"
          onClick={() => onFeedback(node.memory_id, node.text, "up")}
          className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded border border-line hover:border-lime text-lime"
        >
          <ThumbsUp size={13} /> good
        </button>
        <button
          data-testid="fb-down"
          onClick={() => onFeedback(node.memory_id, node.text, "down")}
          className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded border border-line hover:border-amber text-amber"
        >
          <ThumbsDown size={13} /> bad
        </button>
        <button
          data-testid="fb-suppress"
          onClick={() => onFeedback(node.memory_id, node.text, "dismiss", true)}
          className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded border border-line hover:border-rose text-rose ml-auto"
          title="Never link this phrase to this memory"
        >
          <Ban size={13} /> never link
        </button>
      </div>
    </div>
  );
}

function WorkingContext({ sid, memories, bindings, reload }) {
  const [phrase, setPhrase] = useState("");
  const [memId, setMemId] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const memMap = Object.fromEntries(memories.map((m) => [m.id, m]));
  const add = async () => {
    if (!phrase.trim() || !memId) return;
    setBusy(true); setError("");
    try {
      await API.createBinding(sid, { phrase: phrase.trim(), memory_id: memId });
      setPhrase(""); setMemId(""); await reload();
    } catch (_) { setError("Could not save the binding. Please retry."); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { setError(""); await API.deleteBinding(sid, id); await reload(); }
    catch (_) { setError("Could not delete the binding. Please retry."); }
  };
  return (
    <div className="bg-panel border border-line rounded-xl p-4" data-testid="working-context">
      <h3 className="font-display font-semibold text-sm text-muted uppercase tracking-wider mb-3">Working Context</h3>
      <div className="space-y-1.5 mb-3 max-h-40 overflow-auto">
        {bindings.length === 0 && <div className="text-xs text-muted">No bindings yet.</div>}
        {bindings.map((b) => (
          <div key={b.id} className="flex items-center gap-2 text-xs bg-panel2 rounded px-2 py-1.5" data-testid={`binding-row-${b.id}`}>
            <span className="font-mono text-lime">"{b.phrase}"</span>
            <span className="text-muted">→</span>
            <span className="text-amber truncate">{memMap[b.memory_id]?.canonical || b.memory_id}</span>
            <button data-testid={`binding-delete-${b.id}`} title="Delete binding" onClick={() => remove(b.id)} className="ml-auto text-muted hover:text-rose">
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
      <input
        data-testid="binding-phrase-input"
        value={phrase}
        onChange={(e) => setPhrase(e.target.value)}
        placeholder='phrase e.g. "that photo"'
        className="w-full bg-ink border border-line rounded px-2 py-1.5 text-xs mb-1.5 outline-none focus:border-teal/60"
      />
      <div className="flex gap-1.5">
        <select
          data-testid="binding-memory-select"
          value={memId}
          onChange={(e) => setMemId(e.target.value)}
          className="flex-1 bg-ink border border-line rounded px-2 py-1.5 text-xs outline-none focus:border-teal/60"
        >
          <option value="">bind to…</option>
          {memories.map((m) => (
            <option key={m.id} value={m.id}>{m.canonical}</option>
          ))}
        </select>
        <button disabled={busy || !phrase.trim() || !memId} onClick={add} data-testid="binding-add-btn" className="px-2.5 bg-teal/15 text-teal rounded border border-teal/30 hover:bg-teal/25 disabled:opacity-40">
          <Plus size={15} />
        </button>
      </div>
      {error && <div data-testid="binding-error" role="alert" className="text-xs text-rose mt-2">{error}</div>}
    </div>
  );
}

function NotesFeed({ sid, result, text, reloadMemories, rerun }) {
  const candidates = result?.metadata?.memory_candidates || {};
  const list = Object.entries(candidates);
  const [busy, setBusy] = useState(null);
  const [dismissed, setDismissed] = useState([]);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const accept = async (key, c) => {
    setBusy(key);
    setError("");
    try {
      const s = draft?.key === key ? { title: draft.title, description: draft.description, type: c.suggested_type } : (await API.suggestNote(sid, c.text)).suggestion;
      await API.createMemory(sid, {
        type: s.type || c.suggested_type,
        canonical: s.title || c.text,
        description: s.description || "",
        aliases: [],
        how_i_refer_to_it: [],
        pointer: "",
        tags: ["auto-note"],
        cluster_id: c.suggested_cluster_id,
      });
      await reloadMemories();
      setDraft(null);
      rerun();
    } catch (_) {
      setError("Could not save this note. Please try again.");
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="bg-panel border border-line rounded-xl p-4" data-testid="notes-feed">
      <h3 className="font-display font-semibold text-sm text-muted uppercase tracking-wider mb-3 flex items-center gap-2">
        <Sparkles size={14} className="text-amber" /> Note Proposals
      </h3>
      {list.length === 0 && <div className="text-xs text-muted">No proposals. Repeat a novel term to trigger one.</div>}
      <div className="space-y-2 max-h-56 overflow-auto">
        {list.filter(([, c]) => !dismissed.includes(c.text.toLowerCase())).map(([key, c]) => (
          <div key={key} className="bg-panel2 rounded-lg p-2.5" data-testid={`note-proposal-${key}`}>
            <div className="flex items-center justify-between">
              <span className="font-mono text-sm text-fg">{c.text}</span>
              <span className="text-[10px] text-muted font-mono">×{c.occurrences}</span>
            </div>
            <div className="text-[11px] text-muted mb-2">{c.reason}</div>
            {draft?.key === key && <div className="space-y-2 mb-2">
              <input aria-label="Note title" data-testid={`note-title-${key}`} value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="w-full bg-ink border border-line rounded p-2 text-xs" />
              <textarea aria-label="Note description" data-testid={`note-description-${key}`} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="w-full bg-ink border border-line rounded p-2 text-xs" />
            </div>}
            <div className="flex gap-1.5">
              <button
                data-testid={`note-accept-${key}`}
                disabled={busy === key || (draft?.key === key && !draft.title.trim())}
                onClick={() => accept(key, c)}
                className="flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-lime/15 text-lime border border-lime/30 hover:bg-lime/25"
              >
                {busy === key ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Accept
              </button>
              <button data-testid={`note-edit-${key}`} onClick={() => setDraft({ key, title: c.text, description: c.reason })} className="text-[11px] border border-line rounded px-2">Edit</button>
              <button data-testid={`note-dismiss-${key}`} onClick={() => setDismissed((d) => [...d, c.text.toLowerCase()])} className="text-[11px] text-muted ml-auto" title="Dismiss proposal"><X size={13} /></button>
            </div>
          </div>
        ))}
      </div>
      {error && <div data-testid="note-error" role="alert" className="text-xs text-rose mt-2">{error}</div>}
    </div>
  );
}

function ContextPack({ sid, text, strategy, profile, copied, setCopied, emailConfigured, openEmail }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const copy = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await API.contextPack(sid, text, strategy, profile);
      await navigator.clipboard.writeText(r.pack);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      setError("Could not copy the Context Pack. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="bg-panel border border-line rounded-xl p-4" data-testid="context-pack">
      <h3 className="font-display font-semibold text-sm text-muted uppercase tracking-wider mb-3">Context Pack</h3>
      <p className="text-xs text-muted mb-3">Original text plus every link & deixis expanded — paste into an LLM or IDE.</p>
      <div className="flex gap-2">
        <button
          data-testid="copy-pack-btn"
          onClick={copy}
          disabled={loading || !text.trim()}
          className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-amber text-ink font-semibold text-sm hover:brightness-110"
        >
          {loading ? <Loader2 size={15} className="animate-spin" /> : copied ? <Check size={15} /> : <Copy size={15} />}
          {copied ? "Copied!" : "Copy"}
        </button>
        <button
          data-testid="email-pack-btn"
          onClick={openEmail}
          disabled={!emailConfigured}
          title={emailConfigured ? "Email the pack to a verified address" : "Email not configured"}
          className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-line text-fg/80 hover:border-teal disabled:opacity-40"
        >
          <Mail size={15} />
        </button>
      </div>
      {error && <div data-testid="copy-pack-error" role="alert" className="text-xs text-rose mt-2">{error}</div>}
    </div>
  );
}

function EmailModal({ sid, text, strategy, profile, onClose }) {
  const [step, setStep] = useState("email"); // email | code | done
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const request = async () => {
    setBusy(true); setErr("");
    try {
      await API.emailRequestCode(sid, email);
      setStep("code");
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to send code");
    } finally { setBusy(false); }
  };
  const verifyAndSend = async () => {
    setBusy(true); setErr("");
    try {
      await API.emailVerify(sid, email, code);
      await API.emailSendPack(sid, email, text, strategy, profile);
      setStep("done");
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="email-modal">
      <div className="bg-panel border border-line rounded-xl p-5 w-full max-w-md fadein">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg">Email Context Pack</h3>
          <button data-testid="email-close" onClick={onClose} className="text-muted hover:text-fg"><X size={18} /></button>
        </div>
        <p className="text-xs text-muted mb-4">
          We verify you own the address first (double opt-in) before sending — no cold email.
        </p>
        {step === "email" && (
          <>
            <input
              data-testid="email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full bg-ink border border-line rounded-lg px-3 py-2.5 text-sm mb-3 outline-none focus:border-teal/60"
            />
            <button data-testid="email-request-btn" onClick={request} disabled={busy || !email}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-teal text-ink font-semibold hover:brightness-110 disabled:opacity-40">
              {busy ? <Loader2 size={16} className="animate-spin" /> : null} Send verification code
            </button>
          </>
        )}
        {step === "code" && (
          <>
            <p className="text-sm text-fg/80 mb-2">Enter the 6-digit code sent to <span className="text-amber">{email}</span>.</p>
            <input
              data-testid="code-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              className="w-full bg-ink border border-line rounded-lg px-3 py-2.5 text-sm mb-3 outline-none focus:border-teal/60 font-mono tracking-widest text-center"
            />
            <button data-testid="email-verify-btn" onClick={verifyAndSend} disabled={busy || code.length < 6}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-amber text-ink font-semibold hover:brightness-110 disabled:opacity-40">
              {busy ? <Loader2 size={16} className="animate-spin" /> : null} Verify & send pack
            </button>
          </>
        )}
        {step === "done" && (
          <div className="text-center py-4" data-testid="email-done">
            <Check size={40} className="mx-auto text-lime mb-2" />
            <p className="text-fg">Context Pack sent to {email}.</p>
          </div>
        )}
        {err && <div className="text-rose text-xs mt-3" data-testid="email-error">{err}</div>}
      </div>
    </div>
  );
}
